import { useState } from 'react';

import type { AnalyticsPeriod, DateRange } from '@/api/hooks/useAnalytics';
import { useSalesAnalytics } from '@/api/hooks/useAnalytics';
import { downloadUrl } from '@/api/client';
import { Can } from '@/auth/guards';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { HeroStatCard } from '@/features/reports/HeroStatCard';
import { PaymentBreakdownChart } from '@/features/reports/PaymentBreakdownChart';
import { PeriodSelector } from '@/features/reports/PeriodSelector';
import { RankedBarList } from '@/features/reports/RankedBarList';
import { RevenueChart } from '@/features/reports/RevenueChart';
import { money } from '@/lib/utils';
import { Download } from 'lucide-react';

function pctDelta(now: number, prev: number): number {
  if (prev > 0) return Math.round(((now - prev) / prev) * 100);
  return now > 0 ? 100 : 0;
}

export function SalesAnalyticsTab() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [range, setRange] = useState<DateRange>({});

  const { data, isLoading } = useSalesAnalytics(period, range);

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const revenueDelta = pctDelta(data.totals.revenue, data.previous.revenue);
  const txnDelta = pctDelta(data.totals.txns, data.previous.txns);
  const avgBill = data.totals.txns > 0 ? data.totals.revenue / data.totals.txns : 0;
  const topItem = data.topMeds[0];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodSelector period={period} range={range} onPeriodChange={setPeriod} onRangeChange={setRange} />
        <Can permission={PERMISSIONS.salesExport}>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/sales', range.from || range.to ? range : undefined)}>
                <Download /> XLSX
              </a>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={downloadUrl('/api/export/sales/pdf', range.from || range.to ? range : undefined)}>
                <Download /> PDF
              </a>
            </Button>
          </div>
        </Can>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeroStatCard
          label="Revenue"
          value={money(data.totals.revenue)}
          delta={revenueDelta}
          deltaLabel={`vs prior ${data.days}d`}
          gradient="bg-grad-primary"
        />
        <HeroStatCard
          label="Transactions"
          value={String(data.totals.txns)}
          delta={txnDelta}
          deltaLabel="vs prior period"
          gradient="bg-gradient-to-br from-emerald-600 to-emerald-400"
        />
        <HeroStatCard
          label="Avg. bill"
          value={money(avgBill)}
          deltaLabel="per transaction"
          gradient="bg-gradient-to-br from-cyan-600 to-cyan-400"
        />
        <HeroStatCard
          label="Top item"
          value={topItem?.medicineName ?? '—'}
          deltaLabel={topItem ? `${topItem.totalQty} sold` : 'No sales yet'}
          gradient="bg-grad-gold"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Daily revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <RevenueChart data={data.dailyRevenue} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Payment mix</CardTitle>
          </CardHeader>
          <CardContent>
            <PaymentBreakdownChart data={data.paymentBreakdown} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top-selling medicines</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedBarList
              items={data.topMeds.map((m) => ({
                label: m.medicineName,
                value: m.totalQty,
                sublabel: `${m.totalQty} sold · ${money(m.totalRevenue)}`,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Slow-moving stock</CardTitle>
            <p className="text-xs text-muted-foreground">In stock, nothing sold in this period.</p>
          </CardHeader>
          <CardContent>
            {data.slowMoving.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Everything in stock has sold recently.
              </p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.slowMoving.slice(0, 10).map((item) => (
                  <li key={item.id} className="flex items-center justify-between gap-2">
                    <span className="truncate">{item.name}</span>
                    <span className="tabular shrink-0 text-xs text-muted-foreground">
                      {item.stock} {item.unit}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}